/**
 * Hidden ₹1 sanity-test page for verifying the LIVE Razorpay flow end-to-end.
 *
 * - Route is intentionally unlinked from anywhere in the site UX.
 * - Gated by ?key=<TEST_PAY_GATE_KEY>; without it visitors see a 404-style notice.
 * - `noindex` meta + robots so it never lands in search results.
 * - On success the page shows the access token + redirects to /diagnostic so you
 *   can confirm the full chain (Razorpay → /verify → token mint → /diagnostic banner).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RazorpayCheckoutButton from "@/components/pivotbox/RazorpayCheckoutButton";

// Change this if you ever want to invalidate the URL.
const TEST_PAY_GATE_KEY = "pivotbox-2026";

export default function TestPayRs1Page() {
  const [params] = useSearchParams();
  const allowed = params.get("key") === TEST_PAY_GATE_KEY;
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Block search engines from this page even if someone leaks the URL.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Test • Pivotbox";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  if (!allowed) {
    return (
      <div
        style={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'DM Sans', sans-serif",
          color: "#5F5E5A",
          background: "#F5F0E8",
        }}
        data-testid="test-pay-blocked"
      >
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: "#1C1714", fontSize: "2rem", margin: "0 0 0.5rem" }}>404</h1>
          <p style={{ margin: 0 }}>Page not found.</p>
          <Link to="/" style={{ color: "#2F7D57", marginTop: "1rem", display: "inline-block" }}>← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F5F0E8",
        fontFamily: "'DM Sans', sans-serif",
        color: "#1C1714",
        padding: "3.5rem 1.5rem",
      }}
      data-testid="test-pay-page"
    >
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <span
          style={{
            display: "inline-block",
            background: "#d98825",
            color: "#fff",
            padding: "0.25rem 0.7rem",
            borderRadius: 999,
            fontSize: "0.7rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            marginBottom: "1rem",
          }}
        >
          Internal • Live mode
        </span>

        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.25rem", margin: "0 0 0.75rem", color: "#1C1714" }}>
          ₹1 Razorpay live test
        </h1>
        <p style={{ fontSize: "1rem", lineHeight: 1.7, color: "#5F5E5A", margin: "0 0 1.6rem" }}>
          One-time sanity check of the live Razorpay integration. Pays a single rupee, verifies the
          HMAC signature on the backend, mints a real access token, and lets you confirm the
          `/diagnostic?token=…` flow.
        </p>

        <ul
          style={{
            listStyle: "none",
            padding: "1rem 1.2rem",
            margin: "0 0 1.8rem",
            background: "#fff",
            border: "1px solid #D3D1C7",
            borderRadius: 10,
            fontSize: "0.95rem",
            lineHeight: 1.85,
            color: "#1C1714",
          }}
        >
          <li><strong>Amount:</strong> ₹1 (100 paise)</li>
          <li><strong>Mode:</strong> LIVE — uses real card / UPI</li>
          <li><strong>Refund:</strong> issue from Razorpay Dashboard after the test</li>
          <li><strong>On success:</strong> auto-redirects to <code>/diagnostic?token=…</code></li>
        </ul>

        <RazorpayCheckoutButton
          amount={100}
          currency="INR"
          product="live_sanity_test"
          buttonLabel="Pay ₹1 (live) →"
          className="test-pay-btn"
          testId="test-pay-rs1-btn"
          requireDetails
          onSuccess={(r) => setResult(r)}
        />

        {result && (
          <div
            style={{
              marginTop: "1.6rem",
              padding: "1rem 1.2rem",
              background: "rgba(47, 125, 87, 0.08)",
              border: "1px solid rgba(47, 125, 87, 0.35)",
              borderRadius: 10,
              fontSize: "0.9rem",
              wordBreak: "break-all",
            }}
            data-testid="test-pay-rs1-result"
          >
            <strong style={{ color: "#2F7D57" }}>Verified ✓</strong>
            <div style={{ marginTop: 6 }}><strong>payment_id:</strong> {result.payment_id}</div>
            <div><strong>order_id:</strong> {result.order_id}</div>
            <div><strong>access_token:</strong> {result.access_token}</div>
          </div>
        )}

        <p style={{ marginTop: "2rem", fontSize: "0.78rem", color: "#888780" }}>
          This URL is hidden from search engines and not linked anywhere on the public site.
          Append <code>?key={TEST_PAY_GATE_KEY}</code> to access.
        </p>
      </div>
    </div>
  );
}
